import React, { useState, useMemo, useEffect } from 'react';
import { Document as ReactPdfDocument, Page, pdfjs } from 'react-pdf';
import { useApp } from '../../context/AppContext';
import { generateDefaultPageMappings } from '../../utils/pdfUtils';
import { TemplateRole } from '../../types';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

export function Preview() {
  const { state, dispatch } = useApp();
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [templateError, setTemplateError] = useState<string | null>(null);

  // Generate default page mappings when template or document changes
  useEffect(() => {
    if (state.currentDocument && state.selectedTemplate?.isBundle && state.selectedTemplate.pages) {
      const defaultMappings = generateDefaultPageMappings(
        state.currentDocument.pageCount,
        state.selectedTemplate
      );
      dispatch({ type: 'SET_PAGE_TEMPLATE_MAPPINGS', payload: defaultMappings });
    } else {
      // Clear mappings when no bundle or no document
      dispatch({ type: 'SET_PAGE_TEMPLATE_MAPPINGS', payload: [] });
    }
  }, [state.currentDocument, state.selectedTemplate, dispatch]);

  const documentBlob = useMemo(() => {
    if (!state.currentDocument) return null;
    return new Blob([state.currentDocument.pdfData], { type: 'application/pdf' });
  }, [state.currentDocument]);

  // Get the template page blob for the current page
  const getCurrentTemplateBlob = useMemo(() => {
    if (!state.selectedTemplate) return null;

    // If it's a bundle, find the correct page based on mapping
    if (state.selectedTemplate.isBundle && state.selectedTemplate.pages) {
      const mapping = state.pageTemplateMappings.find(m => m.pageNumber === pageNumber);
      if (mapping) {
        const templatePage = state.selectedTemplate.pages.find(p => p.role === mapping.role);
        if (templatePage) {
          return new Blob([templatePage.pdfData], { type: 'application/pdf' });
        }
      }
      // Fallback: use first page if no mapping found
      return new Blob([state.selectedTemplate.pages[0].pdfData], { type: 'application/pdf' });
    }

    // Single template - use the main pdfData
    return new Blob([state.selectedTemplate.pdfData], { type: 'application/pdf' });
  }, [state.selectedTemplate, state.pageTemplateMappings, pageNumber]);

  const templateBlob = getCurrentTemplateBlob;

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    console.log('Preview - Document loaded successfully. numPages from react-pdf:', numPages);
    console.log('Preview - Document pageCount from state:', state.currentDocument?.pageCount);
    setNumPages(numPages);
    setDocumentError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    setDocumentError('Kon document niet laden');
    console.error('PDF load error:', error);
  };

  const onTemplateLoadError = (error: Error) => {
    setTemplateError('Kon template niet laden');
    console.error('Template load error:', error);
  };

  const onTemplateLoadSuccess = () => {
    setTemplateError(null);
    console.log('Template loaded successfully in preview');
  };

  const handleRoleChange = (pageNum: number, newRole: TemplateRole) => {
    dispatch({
      type: 'UPDATE_PAGE_TEMPLATE_MAPPING',
      payload: { pageNumber: pageNum, role: newRole }
    });
  };

  const getCurrentPageRole = (): TemplateRole | null => {
    const mapping = state.pageTemplateMappings.find(m => m.pageNumber === pageNumber);
    return mapping?.role || null;
  };

  const getAvailableRoles = (): TemplateRole[] => {
    if (!state.selectedTemplate?.isBundle || !state.selectedTemplate.pages) {
      return [];
    }
    return state.selectedTemplate.pages.map(p => p.role);
  };

  const getRoleLabel = (role: TemplateRole): string => {
    switch (role) {
      case 'voorblad':
        return 'Voorblad';
      case 'vervolgblad':
        return 'Vervolgblad';
      case 'eindblad':
        return 'Eindblad';
      default:
        return role;
    }
  };

  // Show preview if we have a document OR a template selected
  if (!state.currentDocument && !state.selectedTemplate) {
    return (
      <div className="w-full h-96 flex items-center justify-center bg-gray-50 dark:bg-black rounded-lg border border-gray-200 dark:border-blue-600">
        <div className="text-center text-gray-500 dark:text-white">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-blue-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="text-lg font-medium">Geen content geladen</p>
          <p className="text-sm">Upload een document of selecteer een template</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Voorvertoning</h2>
        
        <div className="flex flex-col space-y-4">
          <div className="flex items-center justify-between">
            {numPages && state.currentDocument && (
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                    disabled={pageNumber <= 1}
                    className="px-3 py-1 bg-gray-200 dark:bg-blue-600 text-gray-700 dark:text-white rounded disabled:opacity-50"
                  >
                    ←
                  </button>
                  <span className="text-sm text-gray-600 dark:text-white">
                    {pageNumber} van {numPages}
                  </span>
                  <button
                    onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
                    disabled={pageNumber >= numPages}
                    className="px-3 py-1 bg-gray-200 dark:bg-blue-600 text-gray-700 dark:text-white rounded disabled:opacity-50"
                  >
                    →
                  </button>
                </div>

                {/* Role selector for bundles */}
                {state.selectedTemplate?.isBundle && state.selectedTemplate.pages && (
                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-gray-600 dark:text-white font-medium">
                      Template blad:
                    </label>
                    <select
                      value={getCurrentPageRole() || ''}
                      onChange={(e) => handleRoleChange(pageNumber, e.target.value as TemplateRole)}
                      className="px-2 py-1 border border-gray-300 dark:border-blue-600 bg-white dark:bg-black text-gray-700 dark:text-white rounded text-sm"
                    >
                      {getAvailableRoles().map((role) => (
                        <option key={role} value={role}>
                          {getRoleLabel(role)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setScale(Math.max(0.5, scale - 0.1))}
                className="px-3 py-1 bg-gray-200 dark:bg-blue-600 text-gray-700 dark:text-white rounded"
              >
                -
              </button>
              <span className="text-sm text-gray-600 dark:text-white min-w-[60px] text-center">
                {Math.round(scale * 100)}%
              </span>
              <button
                onClick={() => setScale(Math.min(2.0, scale + 0.1))}
                className="px-3 py-1 bg-gray-200 dark:bg-blue-600 text-gray-700 dark:text-white rounded"
              >
                +
              </button>
            </div>
          </div>

          {/* Transparency controls temporarily disabled */}
          {false && (
            <div className="flex flex-wrap gap-6 items-center justify-center bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center space-x-3">
                <label className="text-sm font-medium text-gray-700">
                  Document transparantie:
                </label>
                <input
                  type="range"
                  min="0.1"
                  max="1"
                  step="0.1"
                  value={state.documentOpacity}
                  onChange={(e) => dispatch({ 
                    type: 'SET_DOCUMENT_OPACITY', 
                    payload: parseFloat(e.target.value) 
                  })}
                  className="w-24 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="text-sm text-gray-600 min-w-[35px]">
                  {Math.round(state.documentOpacity * 100)}%
                </span>
              </div>

              {state.selectedTemplate && (
                <>
                  <div className="flex items-center space-x-3">
                    <label className="text-sm font-medium text-gray-700">
                      Template transparantie:
                    </label>
                    <input
                      type="range"
                      min="0.1"
                      max="1"
                      step="0.1"
                      value={state.templateOpacity}
                      onChange={(e) => dispatch({ 
                        type: 'SET_TEMPLATE_OPACITY', 
                        payload: parseFloat(e.target.value) 
                      })}
                      className="w-24 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <span className="text-sm text-gray-600 min-w-[35px]">
                      {Math.round(state.templateOpacity * 100)}%
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={state.ignoreWhiteBackground}
                        onChange={() => dispatch({ type: 'TOGGLE_IGNORE_WHITE_BACKGROUND' })}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Wit negeren
                      </span>
                    </label>
                    <span className="text-xs text-gray-500">
                      (Alleen gekleurde delen template tonen)
                    </span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
        <div className="relative flex justify-center bg-gray-100 p-4">
          {documentError ? (
            <div className="h-96 flex items-center justify-center bg-red-50 text-red-600">
              <p>{documentError}</p>
            </div>
          ) : (
            <div className="relative bg-white shadow-lg" style={{ maxWidth: 'fit-content' }}>
              {/* If we have both document and template - layered approach */}
              {state.currentDocument && state.selectedTemplate && (
                <>
                  {/* Template layer - background */}
                  <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{ 
                      opacity: 1.0,
                      zIndex: 1
                    }}
                  >
                    <ReactPdfDocument
                      file={templateBlob}
                      onLoadSuccess={onTemplateLoadSuccess}
                      onLoadError={onTemplateLoadError}
                    >
                      <Page
                        pageNumber={1}
                        scale={scale}
                        width={595}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </ReactPdfDocument>
                  </div>
                  
                  {/* Document layer - foreground with blend mode */}
                  <div 
                    className="relative"
                    style={{ 
                      zIndex: 10,
                      mixBlendMode: 'multiply'
                    }}
                  >
                    <ReactPdfDocument
                      file={documentBlob}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={onDocumentLoadError}
                    >
                      <Page
                        pageNumber={pageNumber}
                        scale={scale}
                        width={595}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </ReactPdfDocument>
                  </div>
                </>
              )}
              
              {/* If we only have a document - show just document */}
              {state.currentDocument && !state.selectedTemplate && documentBlob && (
                <div className="relative">
                  <ReactPdfDocument
                    file={documentBlob}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                  >
                    <Page
                      pageNumber={pageNumber}
                      scale={scale}
                      width={595}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </ReactPdfDocument>
                </div>
              )}
              
              {/* If we only have a template - show just template */}
              {!state.currentDocument && state.selectedTemplate && templateBlob && (
                <div className="relative">
                  <ReactPdfDocument
                    file={templateBlob}
                    onLoadSuccess={onTemplateLoadSuccess}
                    onLoadError={onTemplateLoadError}
                  >
                    <Page
                      pageNumber={1}
                      scale={scale}
                      width={595}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </ReactPdfDocument>
                </div>
              )}
            </div>
          )}
          
          {templateError && (
            <div className="absolute top-2 right-2 bg-red-100 text-red-600 px-3 py-1 rounded text-sm">
              {templateError}
            </div>
          )}
        </div>
        
        <div className="p-4 bg-gray-50 dark:bg-black border-t border-gray-200 dark:border-blue-600">
          <div className="flex justify-between items-center text-sm text-gray-600 dark:text-white">
            {state.currentDocument && (
              <div>
                <span className="font-medium">Document:</span> {state.currentDocument.name}
              </div>
            )}
            {state.selectedTemplate && (
              <div>
                <span className="font-medium">Template:</span> {state.selectedTemplate.name}
              </div>
            )}
            {!state.currentDocument && state.selectedTemplate && (
              <div className="w-full text-center">
                <span className="font-medium">Template:</span> {state.selectedTemplate.name}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}